import { CellReviewProvider } from "@platform/btx-plan-designer";
import {
  DialogProvider,
  useMpxAgGridState,
  useNotification,
} from "@platform/hooks";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router";
import { Alert } from "@platform/design-system";

import { CircularProgressCentered } from "../../../../../../components/CircularProgressCentered";
import { useRfpDetails } from "../../../../../../hooks/useRfpDetails";
import { extractErrorMessage } from "../../../../../../utils";
import { useCarrierSequenceContext } from "../../CarrierSequenceContext";
import { CellReviewCommentDialogRenderer } from "../shared/cellReviews/CellReviewCommentDialogRenderer";
import { useCellReviewContextMenu } from "../shared/cellReviews/useCellReviewContextMenu";

import { CarrierResponsesGrid } from "./CarrierResponsesGrid";
import { applyRowOrder } from "./applyRowOrder";
import { deriveResponsesCellForContextMenu } from "./responsesCellReviewIdentity";
import { useResponsesCellReviews } from "./useResponsesCellReviews";
import { useResponsesTable } from "./useResponsesTable";
import { useResponsesVisibilityGridState } from "./useResponsesVisibilityGridState";
import { useUpdateResponseCell } from "./useUpdateResponseCell";

import type { CarrierResponsesGridStateProps } from "./CarrierResponsesGrid";
import type { UpdateResponseCellRequest } from "@platform/api";
import type { CellValueChangedEvent, ColDef } from "ag-grid-community";
import type { ResponsesGridRow } from "./types";

/** Stable identifier for this grid in mpx grid-states. */
const RESPONSES_GRID_ID = "rfp-responses";

// ---------------------------------------------------------------------------
// Inner component — must live inside CellReviewProvider + DialogProvider
// ---------------------------------------------------------------------------

interface ResponsesGridContentProps {
  rowData: ResponsesGridRow[];
  colDefs: ColDef<ResponsesGridRow>[];
  gridStateProps?: CarrierResponsesGridStateProps;
  onCellValueChanged: (event: CellValueChangedEvent<ResponsesGridRow>) => void;
}

const ResponsesGridContent = ({
  rowData,
  colDefs,
  gridStateProps,
  onCellValueChanged,
}: ResponsesGridContentProps): React.JSX.Element => {
  const getContextMenuItems = useCellReviewContextMenu(
    deriveResponsesCellForContextMenu,
  );
  return (
    <CarrierResponsesGrid
      rowData={rowData}
      colDefs={colDefs}
      gridStateProps={gridStateProps}
      onCellValueChanged={onCellValueChanged}
      getContextMenuItems={getContextMenuItems}
    />
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Responses = (): React.JSX.Element => {
  const { id: rfpId } = useParams<{ id: string }>();
  const {
    data: rfpDetails,
    isLoading: isLoadingDetails,
    error: rfpDetailsError,
  } = useRfpDetails({
    rfpId: rfpId ?? "",
    enabled: !!rfpId,
  });

  const ecoRfpId = rfpDetails?.ecoRfpId ?? "";
  const { rowData, colDefs, isLoading, errorMessage } = useResponsesTable({
    rfpId: ecoRfpId,
    enabled: !!ecoRfpId,
  });

  // Server-side persistence of the user's view (column order/width/
  // pinning/visibility, sort, filter, group). Scoped to (user, ecoRfpId,
  // gridId) by mpx. mpx requires scopeId to be a UUID, so we use the
  // canonical ecoRfpId from rfpDetails — the route `:id` param is the
  // broker-portal numeric id and would 400. Skipped until ecoRfpId is
  // resolved. Row order is not persisted here — carrier order is fixed to
  // the sequence applied from the Quotes tab (CD-1806).
  const { gridProps: mpxGridStateProps, isLoading: isLoadingGridState } =
    useMpxAgGridState({
      scopeType: "rfp",
      scopeId: ecoRfpId,
      gridId: RESPONSES_GRID_ID,
      enabled: !!ecoRfpId,
    });

  // Hidden carriers and coverages are persisted to the shared, per-RFP
  // responses-visibility store rather than per-user localStorage, so every
  // analyst on the RFP — and the Marketing Presentation export — sees the same
  // hidden set. Layered on top of mpxGridStateProps (order/width/sort).
  const { gridProps: gridStateProps, isLoading: isLoadingVisibility } =
    useResponsesVisibilityGridState({
      rfpId: ecoRfpId,
      rows: rowData,
      gridProps: mpxGridStateProps,
      enabled: !!ecoRfpId,
    });

  const { mutate: updateCell } = useUpdateResponseCell({ rfpId: ecoRfpId });
  const { showError } = useNotification();

  const {
    getReview,
    upsertReview,
    clearReview,
    getBrokerHighlight,
    setBrokerHighlight,
    clearBrokerHighlight,
    getEdited,
    setEdited,
  } = useResponsesCellReviews({
    rfpId: ecoRfpId,
    enabled: !!ecoRfpId,
    onSaveError: useCallback(
      (err: Error) => {
        showError(`Failed to save cell review: ${err.message}`);
      },
      [showError],
    ),
  });

  // Derive the carrier/plan-group identity from the AG Grid event and call the
  // mutation. The column field is the coverage's `planGroupId`.
  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent<ResponsesGridRow>) => {
      const carrierId = event.data?.carrierId;
      const planGroupId = event.colDef.field ?? "";
      const manualStatus =
        typeof event.newValue === "string" ? event.newValue : "";

      if (!carrierId || !planGroupId || !manualStatus) return;

      // The cell editor is constrained to MANUAL_RESPONSE_OPTIONS so the runtime
      // values match the enum shapes, but AG Grid returns plain strings from event
      // callbacks. Using `as unknown as` to bridge the string→enum type gap safely.
      const body = {
        carrierId,
        planGroupId,
        manualStatus,
      } as unknown as UpdateResponseCellRequest;

      updateCell({ path: { id: ecoRfpId }, body });
    },
    [ecoRfpId, updateCell],
  );

  // Carrier order the analyst applied to all SBS pages from Quotes (CD-1806).
  // This page has no row drag of its own, so the applied order is always
  // authoritative here — it can't be silently overridden by a local reorder.
  const { appliedToAllCarrierOrder } = useCarrierSequenceContext();

  const orderedRowData = useMemo(
    () => applyRowOrder(rowData, appliedToAllCarrierOrder),
    [rowData, appliedToAllCarrierOrder],
  );

  if (
    isLoadingDetails ||
    isLoading ||
    isLoadingGridState ||
    isLoadingVisibility
  ) {
    return <CircularProgressCentered />;
  }

  if (rfpDetailsError) {
    return (
      <Alert severity="error">
        {extractErrorMessage(rfpDetailsError, "Failed to load RFP details.")}
      </Alert>
    );
  }

  if (errorMessage) {
    return <Alert severity="error">{errorMessage}</Alert>;
  }

  return (
    <DialogProvider>
      <CellReviewProvider
        getReview={getReview}
        upsertReview={upsertReview}
        clearReview={clearReview}
        getBrokerHighlight={getBrokerHighlight}
        setBrokerHighlight={setBrokerHighlight}
        clearBrokerHighlight={clearBrokerHighlight}
        getEdited={getEdited}
        setEdited={setEdited}
      >
        <ResponsesGridContent
          rowData={orderedRowData}
          colDefs={colDefs}
          gridStateProps={gridStateProps}
          onCellValueChanged={handleCellValueChanged}
        />
        <CellReviewCommentDialogRenderer />
      </CellReviewProvider>
    </DialogProvider>
  );
};

export default Responses;
